import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

function CreateOppFromEmailModal({ email, onClose, onCreated }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Extract a keyword from subject for opportunity name
  const subjectKeyword = (email.subject || '')
    .replace(/^(Re|Fw|Fwd):\s*/gi, '')
    .substring(0, 50)
    .trim();

  const [formData, setFormData] = useState({
    name: '',
    deal_value: '',
    stage: 'qualification',
    probability: 10,
    expected_close_date: '',
    priority: 'medium',
    next_step: '',
    next_action_date: '',
    next_action_note: '',
    description: `Created from email: "${email.subject || '(no subject)'}"\nFrom: ${email.from_name || email.from_email}`,
  });

  // Fetch linked lead details to auto-fill
  useEffect(() => {
    const fetchLead = async () => {
      if (!email.lead_id) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/leads/${email.lead_id}`);
        const leadData = res.data;
        setLead(leadData);
        // Auto-fill opportunity name
        const orgName = leadData.organization || leadData.name || '';
        setFormData(prev => ({
          ...prev,
          name: orgName || subjectKeyword || 'New Opportunity',
          deal_value: leadData.estimated_value || leadData.deal_value || '',
        }));
      } catch (err) {
        console.error('Error fetching lead:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [email.lead_id, subjectKeyword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (!email.lead_id) {
        setError('This email is not yet linked to a Lead. Link or create a Lead first.');
        setSaving(false);
        return;
      }
      // Iteration 74: atomic Lead → Opportunity conversion.
      await axios.post(`${API}/leads/${email.lead_id}/convert`, {
        name: formData.name,
        deal_value: parseFloat(formData.deal_value) || 0,
        stage: formData.stage,
        expected_close_date: formData.expected_close_date || null,
        priority: formData.priority,
        next_step: formData.next_step,
        description: formData.description,
      });
      onCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to convert lead');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const stageProbs = { qualification: 10, discovery: 20, meeting: 40, proposal: 60, negotiation: 80 };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-opp-from-email-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Create Opportunity from Email</h2>
              {lead && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Lead: {lead.name} {lead.organization ? `(${lead.organization})` : ''}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="close-opp-modal">
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-3 border-slate-200 border-t-purple-600 rounded-full"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">{error}</div>
            )}

            {/* Email context */}
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium">From email:</span> {email.subject || '(no subject)'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                {email.from_name || email.from_email} · {email.received_at ? new Date(email.received_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Opportunity Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                required
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                data-testid="opp-email-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Deal Value (₹) *</label>
                <input
                  type="number"
                  value={formData.deal_value}
                  onChange={(e) => setFormData(p => ({ ...p, deal_value: e.target.value }))}
                  required
                  placeholder="500000"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                  data-testid="opp-email-value"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Stage</label>
                <select
                  value={formData.stage}
                  onChange={(e) => {
                    const s = e.target.value;
                    setFormData(p => ({ ...p, stage: s, probability: stageProbs[s] || p.probability }));
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  data-testid="opp-email-stage"
                >
                  <option value="qualification">Qualification</option>
                  <option value="discovery">Discovery</option>
                  <option value="meeting">Meeting</option>
                  <option value="proposal">Proposal</option>
                  <option value="negotiation">Negotiation</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Close Date</label>
                <input
                  type="date"
                  value={formData.expected_close_date}
                  onChange={(e) => setFormData(p => ({ ...p, expected_close_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  data-testid="opp-email-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  data-testid="opp-email-priority"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Next Step</label>
              <input
                type="text"
                value={formData.next_step}
                onChange={(e) => setFormData(p => ({ ...p, next_step: e.target.value }))}
                placeholder="e.g., Schedule demo call"
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
              />
            </div>

            {/* NAD */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">Next Action Date *</label>
                  <input type="date" value={formData.next_action_date} onChange={(e) => setFormData(p => ({ ...p, next_action_date: e.target.value }))} required min={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white" data-testid="email-opp-nad" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">Planned Action</label>
                  <input type="text" value={formData.next_action_note} onChange={(e) => setFormData(p => ({ ...p, next_action_note: e.target.value }))} placeholder="e.g., Demo call" className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400" data-testid="email-opp-nad-note" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 text-sm font-medium bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors"
                data-testid="submit-opp-from-email"
              >
                {saving ? 'Creating...' : 'Create Opportunity'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CreateOppFromEmailModal;
